import { z } from 'zod';

import { useAxleMutation, useAxleQuery } from '../apiHelpers';
import { organizationBasicSchema, prioritySchema } from '../sharedSchemas/basicSchemas';
import { qualificationBasicSchema } from '../sharedSchemas/qualificationSchemas';

export const serviceBasicSchema = z.object({
    uuid: z.string().uuid(),
    name: z.string(),
    organization: organizationBasicSchema,
    priority: prioritySchema,
});
export type ServiceBasic = z.infer<typeof serviceBasicSchema>;

export const serviceForListDisplaySchema = serviceBasicSchema.extend({
    duration_minutes: z.number(),
    is_virtual: z.boolean(),
});
export type ServiceForListDisplay = z.infer<typeof serviceForListDisplaySchema>;

export const serviceForDetailDisplaySchema = serviceForListDisplaySchema.extend({
    qualifications: z.array(qualificationBasicSchema),
    description: z.string(),
    instructions: z.string(),
    requires_all_qualifications: z.boolean(),
});

export const useServices = () =>
    useAxleQuery({
        queryKey: ['services', 'all'],
        path: '/services',
        suspense: true,
        responseSchema: z.object({
            services: z.array(
                serviceForListDisplaySchema.extend({
                    qualifications: z.array(qualificationBasicSchema),
                }),
            ),
        }),
    }).data.services;

export const useServiceOptions = () => {
    return useAxleQuery({
        queryKey: ['services', 'options'],
        path: '/services/options',
        suspense: true,
        responseSchema: z.object({
            organization_options: z.array(organizationBasicSchema),
            qualification_options_by_org_uuid: z.record(z.array(qualificationBasicSchema)),
            priority_options: z.array(prioritySchema),
        }),
        transform: (data) => ({
            orgOptions: data.organization_options,
            qualificationOptionsByOrgId: data.qualification_options_by_org_uuid,
            priorityOptions: data.priority_options,
        }),
    }).data;
};

export const useDeleteService = (service_uuid: string) =>
    useAxleMutation({
        axiosConfig: () => ({
            method: 'post',
            path: `/services/${service_uuid}/delete`,
        }),
        softResetQueries: [
            ['services', 'all'],
            ['services', service_uuid],
        ],
    });
